/**
 * Product data fetching and transformation utilities
 * Retrieves and formats product pricing and variant information from Storefront API
 */

import { globalState } from '@/components/helpers/utils'
import { QUERY_PRODUCT_PRICE } from './queries'
import { storefront } from 'libs/storefront/client'

/**
 * Product interface representing the structure of product data
 */
interface Product {
  handle: string;
  variants: Array<any>;
  [key: string]: any;
}

/**
 * ProductPrice interface for the simplified product price structure
 */
interface ProductPrice {
  title: string;
  handle: string;
  price: string;
}

/**
 * Fetches product prices for a given country
 * @param results - Array of product results to get prices for
 * @returns Promise containing simplified product prices or null if error
 */
async function getProductPrices(results: Product[]): Promise<ProductPrice[] | null> {
  // Validate inputs
  if (!Array.isArray(results) || results.length === 0) {
    console.error('Invalid or empty results array')
    return null
  }

  // Check for country code
  if (!globalState?.localization?.country) {
    console.warn('Country code not found in global state')
    return null
  }

  try {
    const country = globalState.localization.country
    const resultCount = results.length
    const query = getProductIDs(results)

    // Execute query
    const productPriceResponse = await storefront.query({
      query: QUERY_PRODUCT_PRICE,
      variables: {
        country,
        query,
        count: resultCount
      }
    })

    // Check for errors in response
    if (productPriceResponse.errors) {
      throw new Error(JSON.stringify(productPriceResponse.errors))
    }

    // Check if response contains expected data structure
    if (!productPriceResponse.data?.products?.edges) {
      throw new Error('Unexpected response structure')
    }

    return simplifyProductPrices(productPriceResponse)
  } catch (error) {
    console.error('Error fetching product prices:', error)
    return null
  }
}

/**
 * Extracts product handles and joins them with OR for the query
 * @param results - Array of product results
 * @returns String of product handles joined with OR operator
 */
const getProductIDs = (results: Product[]): string => {
  if (!Array.isArray(results)) return ''

  const idArray = results.map((result: Product) => result.uid).filter(Boolean) // Filter out any undefined or empty handles

  return idArray.map((id: string) => `(id:"${id.split('/').pop()}")`).join(' OR ')
}

/**
 * Builds a query string for product handles
 * @param handles - String of product handles
 * @returns Formatted query string
 */
const buildQuery = (handles: string): string => {
  if (!handles) return ''
  return `handle:${handles}`
}

/**
 * Simplifies product price data from the API response
 * @param productPriceResponse - API response with product price data
 * @returns Array of simplified product price objects
 */
const simplifyProductPrices = (productPriceResponse: any): ProductPrice[] => {
  const edges = productPriceResponse?.data?.products?.edges || []

  return edges.map((product: any) => {
    const node = product?.node || {}
    const firstVariantEdge = node?.variants?.edges?.[0] || {}
    const firstVariantNode = firstVariantEdge?.node || {}

    const price = firstVariantNode?.price?.amount || '0'

    return {
      title: node.title || '',
      handle: node.handle || '',
      id: node.id || '',
      price
    }
  })
}

export { getProductPrices, getProductIDs, buildQuery, simplifyProductPrices }
